import {
  deleteObject,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';

import { FileVisibility } from '../shared/constants';
import { IAppFile } from '../types/interfaces/app-file';
import { INewFile } from '../types/interfaces/new-file';
import { dataUrlToBlob } from './file-conversion-utils';

async function generateFileNameHash(
  originalFileName: string,
  timestamp: number,
): Promise<string> {
  const randomStr = Math.random().toString(36).substring(2, 15);
  const data = `${originalFileName}-${timestamp}-${randomStr}`;

  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  const extension = originalFileName.split('.').pop() || '';
  return `${hashHex.substring(0, 32)}.${extension}`;
}

export async function uploadFileToFirebase(
  file: File | Blob,
  storagePath: string,
  isPreview = false,
  originalFileName?: string,
): Promise<string> {
  const storage = getStorage();
  const timestamp = Date.now();

  const fileName =
    originalFileName ||
    (file instanceof File ? file.name : `file-${timestamp}`);
  const hashedFileName = await generateFileNameHash(fileName, timestamp);

  const pathParts = [storagePath, FileVisibility.public];
  if (isPreview) {
    pathParts.push('preview');
  }
  pathParts.push(hashedFileName);

  const fullStoragePath = pathParts.join('/');
  const storageRef = ref(storage, fullStoragePath);

  const metadata = {
    cacheControl: 'public, max-age=604800',
    contentType: file instanceof File ? file.type : 'application/octet-stream',
  };

  await new Promise<void>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      'state_changed',
      () => {},
      (error) => {
        reject(error);
      },
      () => {
        resolve();
      },
    );
  });

  return fullStoragePath;
}

export async function uploadFiles(
  file: File | Blob,
  storagePath: string,
  previewFile?: File | Blob,
  originalFileName?: string,
  previewOriginalFileName?: string,
): Promise<{ storagePath: string; previewStoragePath?: string }> {
  const mainStoragePath = await uploadFileToFirebase(
    file,
    storagePath,
    false,
    originalFileName,
  );

  let previewStoragePath: string | undefined;
  if (previewFile) {
    previewStoragePath = await uploadFileToFirebase(
      previewFile,
      storagePath,
      true,
      previewOriginalFileName || originalFileName,
    );
  }

  return {
    storagePath: mainStoragePath,
    previewStoragePath,
  };
}

export async function deleteFileFromFirebase(
  storagePath: string,
): Promise<void> {
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  await deleteObject(storageRef);
}

export async function deleteFilesFromFirebase(
  storagePaths: string[],
): Promise<void> {
  await Promise.all(
    storagePaths.map((path) => deleteFileFromFirebase(path).catch(() => {})),
  );
}

export async function uploadFileAndCreateAppFile(
  file: INewFile,
  storagePath: string,
): Promise<IAppFile> {
  const fileBlob = dataUrlToBlob(file.fileBlob);
  const previewBlob = file.previewFileBlob
    ? dataUrlToBlob(file.previewFileBlob)
    : undefined;

  const { storagePath: uploadedStoragePath, previewStoragePath } =
    await uploadFiles(fileBlob, storagePath, previewBlob, file.name);

  const uploadedFile: IAppFile = {
    id: file.id?.toString(),
    name: file.name,
    mimeType: file.mimeType,
    createdAt: file.createdAt,
    storageLink: uploadedStoragePath,
    previewStorageLink: previewStoragePath,
  };

  return uploadedFile;
}

export async function uploadAvatarFile(
  file: INewFile,
  storagePath: string,
): Promise<string> {
  const fileBlob = dataUrlToBlob(file.fileBlob);
  const storageLink = await uploadFileToFirebase(
    fileBlob,
    storagePath,
    false,
    file.name,
  );

  return storageLink;
}
